import { MouseEventHandler, useEffect, useRef, useState } from "react";
import Time from "../Time";
import { getLatestUpdate } from "../../pages/ViewCompany/utils";
import { Contributors } from "../Contributors";
import { addKeySource } from "../../redux/modules/companyDetails/actions";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectCompanyDetails } from "../../redux/modules/companyDetails/selector";
import { reset } from "../../redux/modules/companyDetails/slice";
import NotificationToast from "../NotificationToast";
import { userSelector } from "../../redux/modules/auth/userSelector";
import { normalizeUrl } from "../ViewAddKeySources/utils";

export default function KeySources(props: {
  selectTab: MouseEventHandler<any>;
  keySources: any[];
  contributors?: string[];
  /**
   * When provided (create-company mode), adding a link updates local state
   * instead of dispatching to the API (which requires an existing company id).
   */
  setKeySources?: Function;
}) {
  const { selectTab, keySources, contributors, setKeySources } = props;

  const user: any = useSelector(userSelector);

  const [newKeySource, setNewKeySource] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const isDropping = useRef(false);

  const latestUpdate = getLatestUpdate(keySources);
  let lastUpdate = "";

  if (latestUpdate) {
    const { time, date, day } = latestUpdate;
    lastUpdate = `${date} ${time}`;
  }

  const { pathname } = useLocation();
  const dispatch = useDispatch<any>();
  // companyId is only valid when viewing/editing an existing company
  const companyId = Number(pathname.split("/")[3]);

  /**
   * Save a link.
   * - In create-company mode (setKeySources is provided): append to local state.
   * - In view/edit mode: dispatch to the API as before.
   */
  const handleLinkChange = (source: string) => {
    if (!source.trim()) return;

    if (setKeySources) {
      // Create-company mode — just update the local list
      setKeySources((prev: any[]) => [...prev, { source }]);
      setNewKeySource("");
    } else {
      // Existing-company mode — persist via API
      setIsLoading(true);
      dispatch(addKeySource({ companyId, source }));
    }
  };

  const { status, message } = useSelector(selectCompanyDetails);
  useEffect(() => {
    if (status === "success") {
      setIsLoading(false);
      setNewKeySource("");

      const t = setTimeout(() => {
        dispatch(reset());
      }, 5000);

      return () => {
        dispatch(reset());
        clearTimeout(t);
      };
    }
  }, [status, dispatch]);

  return (
    <>
    <div className="key-sources mt-3 mb-3">
      <div className="card w-100">
        <div className="card-body">
          <div className="top">
            <div className="top-start">
              <div>
                <h5 className="card-title">Key Sources and Links&nbsp;</h5>
              </div>
              <div>
                <Time time={lastUpdate} />
              </div>
            </div>
            <div className="top-end">
              <div>
                <span role="button" onClick={selectTab}>
                  <img src="/img/Vector.png" className="pl-3" alt="" />
                </span>
              </div>
            </div>
          </div>

          <div className="bg mt-2">
            <div className="bg-inner scroll-nugget mt-2">
              {user?.role === 'admin' ? (
                <input
                  value={newKeySource}
                  placeholder="Drop sources and links to save"
                  onChange={(e) => {
                    if (!isDropping.current) setNewKeySource(e.target.value);
                  }}
                  onBlur={(e) => {
                    // Don't process blur immediately after a drop — drop already handles save
                    if (isDropping.current) return;
                    const normalized = normalizeUrl(e.target.value);
                    setNewKeySource(normalized);
                    if (normalized) handleLinkChange(normalized);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    isDropping.current = true;
                    const dropped =
                      e.dataTransfer.getData("text/uri-list") ||
                      e.dataTransfer.getData("text/plain");
                    const normalized = normalizeUrl(dropped);
                    setNewKeySource(normalized);
                    if (normalized) handleLinkChange(normalized);
                    // Keep flag active long enough to suppress the onBlur that fires after drop
                    setTimeout(() => { isDropping.current = false; }, 300);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const normalized = normalizeUrl(newKeySource);
                      if (normalized) handleLinkChange(normalized);
                    }
                  }}
                />
              ) : null}
              {keySources.map((keysource, i) => (
                <p
                  key={keysource.id ?? i}
                  className="p-2 border rounded my-3"
                  style={{ backgroundColor: "lightgrey" }}
                >
                  {keysource.source}
                </p>
              ))}
            </div>
            <div className="bg-inner mt-2">
              <p className="key">
                Key Contributors &gt;
                <span>
                  <Contributors contributors={contributors ?? []} />
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
      {/* Only show the toast in existing-company mode where API calls are made */}
      {!setKeySources && (
        <NotificationToast message={message} show={status === "success"} />
      )}
    </>
  );
}
